// The organisation's assets at a glance, grouped by site, with live values and statuses: as tiles, as one wide
// block per asset with its temperature curve (24 hours or 7 days, with the alarm thresholds), or on a map of the
// sites and trucks (OverviewMap.tsx).

import type { AlarmRule, Asset, AssetStatus, Probe } from '@cck/schema';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { type Threshold, TemperatureChart } from '../components/Chart.tsx';
import { CleaningTile, useCleaningBySite } from '../components/CleaningTile.tsx';
import { AssetTypeLabel, STATUS_BORDER, StatusBadge } from '../components/domain.tsx';
import { useCurrentOrg, useReadOnly, useUser } from '../components/Shell.tsx';
import { Alert, cx, EmptyState, LinkButton, PageHeader, Select, Spinner } from '../components/ui.tsx';
import { useI18n } from '../i18n/index.ts';
import { formatAgo, formatDuration, formatTemp } from '../lib/format.ts';
import { useAlarms, useAssets, useOverview, useProbes, useRules, useSeries, useSites } from '../lib/queries.ts';
import { markTourSeen, shouldAutoStart, tourSeen } from '../lib/tour.ts';
import { OverviewMap } from './OverviewMap.tsx';

const RANK = { alarm: 0, offline: 1, warning: 2, ok: 3, no_data: 4 } as const;
const VIEW_KEY = 'cck-overview-view';
const CHART_RANGES = { '24h': 1, '7d': 7 } as const;
type ChartRange = keyof typeof CHART_RANGES;
const VIEWS = ['cards', 'charts', 'map'] as const;
type View = typeof VIEWS[number];
const VIEW_LABEL = { cards: 'overview.viewCards', charts: 'overview.viewCharts', map: 'overview.viewMap' } as const;
const isView = (v: string | null): v is View => (VIEWS as readonly (string | null)[]).includes(v);

function storedView(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return isView(v) ? v : 'cards';
  } catch {
    return 'cards';
  }
}

export function Overview() {
  const { orgId, can } = useCurrentOrg();
  const { t } = useI18n();
  const overview = useOverview(orgId);
  const assets = useAssets(orgId);
  const sites = useSites(orgId);
  const probes = useProbes(orgId);
  const open = useAlarms(orgId, 'open=true', 100);
  const [siteFilter, setSiteFilter] = useState('');
  const [params, setParams] = useSearchParams();
  const asked = params.get('view');
  const view: View = isView(asked) ? asked : asked === null ? storedView() : 'cards';
  const setView = (v: View) => {
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode: this page only */ }
    setParams((p) => { p.set('view', v); return p; }, { replace: true });
  };
  const [range, setRange] = useState<ChartRange>('24h');
  const rules = useRules(orgId, '', view === 'charts');
  const cleaning = useCleaningBySite(orgId);
  const me = useUser();
  const readOnly = useReadOnly();
  const navigate = useNavigate();

  // The guided tour opens by itself once per user and browser, when there is something to show (Shell.tsx runs it).
  const hasSites = (sites.data?.data.length ?? 0) > 0;
  useEffect(() => {
    if (assets.isPending || !hasSites) return;
    if (!shouldAutoStart({ seen: tourSeen(me.id), readOnly, kiosk: !!me.kiosk, impersonating: !!me.impersonation })) return;
    markTourSeen(me.id);
    navigate({ search: location.search }, { replace: true, state: { tour: true } });
  }, [assets.isPending, hasSites, me, readOnly, navigate]);

  const status = useMemo(() => new Map((overview.data?.assets ?? []).map((a) => [a.asset_id, a])), [overview.data]);
  const probeLabel = useMemo(() => new Map((probes.data?.data ?? []).map((p) => [p.id, p.label ?? p.channel])), [probes.data]);
  const tempsByAsset = useMemo(() => {
    const m = new Map<string, Probe[]>();
    for (const p of probes.data?.data ?? []) {
      if (p.kind !== 'temperature' || p.disabled_at || !p.asset_id) continue;
      m.set(p.asset_id, [...(m.get(p.asset_id) ?? []), p]);
    }
    return m;
  }, [probes.data]);
  const rulesByAsset = useMemo(() => {
    const assetOfProbe = new Map((probes.data?.data ?? []).map((p) => [p.id, p.asset_id]));
    const m = new Map<string, AlarmRule[]>();
    for (const r of rules.data?.data ?? []) {
      const assetId = r.scope.asset_id ?? (r.scope.probe_id ? assetOfProbe.get(r.scope.probe_id) : null);
      if (assetId) m.set(assetId, [...(m.get(assetId) ?? []), r]);
    }
    return m;
  }, [rules.data, probes.data]);
  const window = useMemo(() => {
    // Rounded to the minute, so that the queries stay the same between renders.
    const to = Math.ceil(Date.now() / 60_000) * 60_000;
    return { from: new Date(to - CHART_RANGES[range] * 86_400_000).toISOString(), to: new Date(to).toISOString() };
  }, [range]);

  if (assets.isPending || sites.isPending) return <Spinner />;
  const siteList = sites.data?.data ?? [];
  const assetList = assets.data?.data ?? [];
  // A new organisation starts in the setup assistant; until its probes measure equipment, the overview offers it.
  if (siteList.length === 0 && can('admin')) return <Navigate to="setup" replace />;
  const unfinished = can('admin') && probes.data !== undefined && !probes.data.data.some((p) => p.asset_id);
  const bySite = siteList.filter((s) => !siteFilter || s.id === siteFilter).map((s) => ({
    site: s,
    assets: assetList.filter((a) => a.site_id === s.id)
      .sort((a, b) => RANK[status.get(a.id)?.status ?? 'no_data'] - RANK[status.get(b.id)?.status ?? 'no_data']
        || a.name.localeCompare(b.name)),
  })).filter((g) => g.assets.length > 0 || siteFilter);
  const active = (open.data?.data ?? []).filter((a) => a.condition === 'active');
  const awaiting = (open.data?.data ?? []).filter((a) => a.condition === 'cleared' && !a.acknowledged_at);

  return (
    <div>
      <PageHeader title={t('overview.title')}
        subtitle={overview.data ? t(overview.data.source === 'live' ? 'overview.live' : 'overview.fromDatabase') : undefined}
        actions={(
          <>
            {siteList.length > 1 && (
              <Select aria-label={t('overview.site')} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} className="w-auto">
                <option value="">{t('overview.allSites')}</option>
                {siteList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            )}
            {assetList.length > 0 && (
              <div role="group" aria-label={t('overview.view')} data-tour="views" className="flex overflow-hidden rounded-md border border-slate-300">
                {VIEWS.map((v) => (
                  <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
                    className={cx('px-2.5 py-1.5 text-xs font-medium', view === v ? 'bg-brand-600 text-on-brand' : 'bg-white text-slate-700 hover:bg-slate-50')}>
                    {t(VIEW_LABEL[v])}
                  </button>
                ))}
              </div>
            )}
            {view === 'charts' && (
              <div role="group" aria-label={t('assets.range')} className="flex overflow-hidden rounded-md border border-slate-300">
                {(Object.keys(CHART_RANGES) as ChartRange[]).map((r) => (
                  <button key={r} type="button" aria-pressed={range === r} onClick={() => setRange(r)}
                    className={cx('px-2.5 py-1.5 text-xs font-medium', range === r ? 'bg-brand-600 text-on-brand' : 'bg-white text-slate-700 hover:bg-slate-50')}>
                    {t(`ranges.${r}`)}
                  </button>
                ))}
              </div>
            )}
            {can('admin') && <LinkButton to="devices/claim" variant="secondary">{t('devices.claim')}</LinkButton>}
          </>
        )} />

      {unfinished && (
        <div className="mb-5">
          <Alert tone="blue" title={t('setup.resumeTitle')} action={<LinkButton to="setup" size="sm">{t('setup.resume')}</LinkButton>}>
            {t('setup.resumeHint')}
          </Alert>
        </div>
      )}

      {(active.length > 0 || awaiting.length > 0) && (
        <div className="mb-5" data-tour="alarm-banner">
          <Alert tone={active.length > 0 ? 'red' : 'amber'}
            title={active.length > 0 ? t('overview.activeAlarms', { count: active.length }) : t('overview.awaiting', { count: awaiting.length })}
            action={<LinkButton to="alarms?open=true" size="sm" variant="secondary">{t('overview.seeAlarms')}</LinkButton>}>
            {t('overview.alarmsHint')}
          </Alert>
        </div>
      )}

      {siteList.length === 0 && (
        <EmptyState title={t('overview.noSites')} action={can('admin') && (
          <LinkButton to="settings/sites">{t('overview.addSite')}</LinkButton>
        )}>{t('overview.noSitesHint')}</EmptyState>
      )}
      {siteList.length > 0 && assetList.length === 0 && (
        <EmptyState title={t('overview.noAssets')} action={can('admin') && (
          <LinkButton to="settings/sites">{t('overview.addAsset')}</LinkButton>
        )}>{t('overview.noAssetsHint')}</EmptyState>
      )}

      {view === 'map' && assetList.length > 0 && (
        <OverviewMap sites={siteList.filter((s) => !siteFilter || s.id === siteFilter)} assets={assetList.filter((a) => !siteFilter || a.site_id === siteFilter)} status={status}
          probeLabel={probeLabel} alarms={open.data?.data ?? []} admin={can('admin')} />
      )}

      <div className="flex flex-col gap-8">
        {view !== 'map' && bySite.map(({ site, assets: list }) => (
          <section key={site.id} aria-labelledby={`site-${site.id}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 id={`site-${site.id}`} className="text-sm font-semibold uppercase tracking-wide text-slate-500">{site.name}</h2>
              <CleaningTile day={cleaning.get(site.id)} />
            </div>
            {list.length === 0 ? <p className="text-sm text-slate-500">{t('overview.siteEmpty')}</p> : view === 'charts' ? (
              <div className="flex flex-col gap-3">
                {list.map((a) => (
                  <AssetChartBlock key={a.id} orgId={orgId} asset={a} status={status.get(a.id)} probes={tempsByAsset.get(a.id) ?? []}
                    rules={rulesByAsset.get(a.id) ?? []} probeLabel={probeLabel} from={window.from} to={window.to}
                    timeZone={site.timezone} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {list.map((a) => <AssetCard key={a.id} asset={a} status={status.get(a.id)} probeLabel={probeLabel} />)}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

function AssetCard({ asset, status, probeLabel }: { asset: Asset; status: AssetStatus | undefined; probeLabel: Map<string, string> }) {
  const { t, lang } = useI18n();
  const s = status?.status ?? 'no_data';
  const [first, ...others] = status?.probes ?? [];
  return (
    <Link to={`assets/${asset.id}`} data-tour="asset"
      className={cx('block rounded-lg border border-l-4 border-slate-200 bg-white p-4 shadow-xs transition hover:shadow-md', STATUS_BORDER[s])}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-slate-900">{asset.name}</p>
          <p className="text-xs text-slate-500"><AssetTypeLabel type={asset.type} label={asset.type_label} /></p>
        </div>
        <StatusBadge status={s} />
      </div>
      <p className={cx('mt-3 text-3xl font-semibold tabular-nums', s === 'alarm' ? 'text-red-700' : 'text-slate-900')}>
        {formatTemp(first?.value_c, lang)}
      </p>
      {others.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
          {others.map((p) => (
            <li key={p.probe_id} className="flex justify-between gap-2">
              <span className="truncate">{probeLabel.get(p.probe_id) ?? '—'}</span>
              <span className="tabular-nums">{formatTemp(p.value_c, lang)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
        {status?.door_open_since && (
          <span className="font-medium text-amber-700">
            {t('overview.doorOpen', { duration: formatDuration((Date.now() - Date.parse(status.door_open_since)) / 1000, lang) })}
          </span>
        )}
        {status?.awaiting_acknowledgement ? (
          <span className="font-medium text-amber-700">{t('overview.toAcknowledge', { count: status.awaiting_acknowledgement })}</span>
        ) : null}
        <span>{status?.last_seen_at ? t('overview.seen', { ago: formatAgo(status.last_seen_at, lang) }) : t('overview.neverSeen')}</span>
        {status?.position && <span>{t('overview.position', { ago: formatAgo(status.position.at, lang) })}</span>}
      </div>
    </Link>
  );
}

/** Whether the element has come near the screen once: charts load only then, so a long list stays light. */
function useSeen<T extends Element>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) setSeen(true); }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

function AssetChartBlock({ orgId, asset, status, probes, rules, probeLabel, from, to, timeZone }: {
  orgId: string; asset: Asset; status: AssetStatus | undefined; probes: Probe[]; rules: AlarmRule[];
  probeLabel: Map<string, string>; from: string; to: string; timeZone: string;
}) {
  const { t, lang } = useI18n();
  const [ref, seen] = useSeen<HTMLElement>();
  const series = useSeries(orgId, seen ? probes.map((p) => p.id) : [], from, to);
  const s = status?.status ?? 'no_data';
  const thresholds = useMemo<Threshold[]>(() => rules
    .filter((r): r is AlarmRule & { type: 'high' | 'low' } => (r.type === 'high' || r.type === 'low') && r.enabled)
    .map((r) => ({ value: r.params.threshold_c, color: r.severity === 'critical' ? '#dc2626' : '#d97706',
      label: t(r.type === 'high' ? 'assets.thresholdHigh' : 'assets.thresholdLow', { value: formatTemp(r.params.threshold_c, lang) }) })),
  [rules, lang, t]);
  const labels = Object.fromEntries(probes.map((p) => [p.id, probeLabel.get(p.id) ?? p.channel]));
  return (
    <section ref={ref} aria-label={asset.name} data-tour="asset"
      className={cx('rounded-lg border border-l-4 border-slate-200 bg-white p-4 shadow-xs', STATUS_BORDER[s])}>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <Link to={`assets/${asset.id}`} className="font-medium text-slate-900 hover:underline">{asset.name}</Link>
          <p className="text-xs text-slate-500"><AssetTypeLabel type={asset.type} label={asset.type_label} /></p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {(status?.probes ?? []).map((p) => (
            <span key={p.probe_id} className="text-sm text-slate-600">
              {(status?.probes.length ?? 0) > 1 && <span className="mr-1">{probeLabel.get(p.probe_id) ?? '—'}</span>}
              <span className={cx('text-xl font-semibold tabular-nums', s === 'alarm' ? 'text-red-700' : 'text-slate-900')}>
                {formatTemp(p.value_c, lang)}
              </span>
            </span>
          ))}
          <StatusBadge status={s} />
        </div>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
        {status?.door_open_since && (
          <span className="font-medium text-amber-700">
            {t('overview.doorOpen', { duration: formatDuration((Date.now() - Date.parse(status.door_open_since)) / 1000, lang) })}
          </span>
        )}
        {status?.awaiting_acknowledgement ? (
          <span className="font-medium text-amber-700">{t('overview.toAcknowledge', { count: status.awaiting_acknowledgement })}</span>
        ) : null}
        <span>{status?.last_seen_at ? t('overview.seen', { ago: formatAgo(status.last_seen_at, lang) }) : t('overview.neverSeen')}</span>
      </div>
      <div className="mt-3 min-h-[220px]">
        {probes.length === 0 && <p className="py-16 text-center text-sm text-slate-500">{t('assets.noProbes')}</p>}
        {probes.length > 0 && (!seen || series.isPending) && <div className="py-16"><Spinner /></div>}
        {series.isError && <Alert tone="red">{t('errors.unavailable')}</Alert>}
        {series.data && (series.data.series.length === 0
          ? <p className="py-16 text-center text-sm text-slate-500">{t('assets.noData')}</p>
          : <TemperatureChart data={series.data} labels={labels} thresholds={thresholds} timeZone={timeZone} lang={lang} height={220} />)}
      </div>
    </section>
  );
}
